import { useState } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import PublicReveal from './PublicReveal';
import type { FiliereContenuPedagogique } from '../../utils/filiereContenuPedagogique';
import '../../styles/publicFiliereProgramme.css';

type Props = {
  contenu: FiliereContenuPedagogique;
};

/**
 * Programme public inspiré d’une fiche formation type onglets modules + listes.
 * @see https://www.kadea.academy/formations/dev-web/?city=kinshasa
 */
export default function PublicFiliereProgrammeSection({ contenu }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const modules = contenu.modules;
  const active = modules[activeIndex] ?? modules[0] ?? null;

  return (
    <section id="contenu-formation" className="public-filiere-programme scroll-mt-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <PublicReveal animation="fade-in-up">
          <p className="public-filiere-detail-eyebrow">Programme de formation</p>
          <h2 className="public-filiere-detail-title">Contenu de la formation</h2>
          {contenu.programmeIntro ? (
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-on-surface-variant">
              {contenu.programmeIntro}
            </p>
          ) : null}
        </PublicReveal>

        {contenu.technologies.length > 0 ? (
          <PublicReveal animation="fade-in-up" delay={60} className="public-filiere-programme__stack">
            <p className="public-filiere-programme__stack-label">
              {contenu.technologiesLabel?.trim() || 'Technologies abordées'}
            </p>
            <ul className="public-filiere-programme__tech">
              {contenu.technologies.map((tech) => (
                <li key={tech}>
                  <span className="public-filiere-programme__item-icon" aria-hidden>
                    <LucideSymbolIcon name="check_circle" tone="primary" size={18} />
                  </span>
                  <span>{tech}</span>
                </li>
              ))}
            </ul>
          </PublicReveal>
        ) : null}

        {modules.length > 0 && active ? (
          <div className="public-filiere-programme__board">
            <PublicReveal animation="fade-in-left" className="public-filiere-programme__nav">
              <p className="public-filiere-programme__nav-label">Modules</p>
              <div className="public-filiere-programme__tabs" role="tablist" aria-label="Modules du programme">
                {modules.map((module, index) => {
                  const selected = index === activeIndex;
                  return (
                    <button
                      key={`${module.title}-${index}`}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      className={`public-filiere-programme__tab ${selected ? 'is-active' : ''}`}
                      onClick={() => setActiveIndex(index)}
                    >
                      <span className="public-filiere-programme__tab-index">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="public-filiere-programme__tab-title">{module.title}</span>
                    </button>
                  );
                })}
              </div>
            </PublicReveal>

            <PublicReveal animation="fade-in-right" delay={80} className="public-filiere-programme__panel">
              <div role="tabpanel">
                <h3 className="public-filiere-programme__panel-title">{active.title}</h3>
                {active.items.length > 0 ? (
                  <ul className="public-filiere-programme__items">
                    {active.items.map((item) => (
                      <li key={item}>
                        <span className="public-filiere-programme__item-icon" aria-hidden>
                          <LucideSymbolIcon name="check_circle" tone="primary" size={18} />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="public-filiere-programme__empty">
                    Le détail de ce module sera bientôt publié.
                  </p>
                )}
              </div>
            </PublicReveal>
          </div>
        ) : null}
      </div>
    </section>
  );
}
